import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from "react";
import { useTheme } from "./ThemeContext"; // Import useTheme

// 1. Define the interface for a background option
export interface BackgroundOption {
    id: string;
    name: string;
    lightSvgVar: string; // CSS variable name for light mode SVG URL
    darkSvgVar: string; // CSS variable name for dark mode SVG URL
    previewLightSvg: string; // SVG data URL for light mode preview
    previewDarkSvg: string; // SVG data URL for dark mode preview
    isDynamic?: boolean; // Flag for dynamically generated SVGs
}

// Helper function to generate plain gradient SVG
const generatePlainGradientSvg = (primaryColor: string, isDarkMode: boolean): string => {
    const topColor = isDarkMode ? "#333333" : "#f0f0f0";
    const bottomColor = primaryColor;

    const svgString = `
        <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
            <defs>
                <linearGradient id="grad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="75%" style="stop-color:${topColor};stop-opacity:1" />
                    <stop offset="100%" style="stop-color:${bottomColor};stop-opacity:1" />
                </linearGradient>
            </defs>
            <rect width="100%" height="100%" fill="url(#grad)" />
        </svg>
    `;
    return `url("data:image/svg+xml,${encodeURIComponent(svgString)}")`;
};

// 2. Define the available background options
const staticBackgroundOptions: Omit<BackgroundOption, "previewLightSvg" | "previewDarkSvg">[] = [
    {
        id: "triangular",
        name: "Honey Prism",
        lightSvgVar: "--triangular-geometric-light-bg",
        darkSvgVar: "--triangular-geometric-dark-bg",
    },
    {
        id: "cosmic_rain",
        name: "Cosmic Rain",
        lightSvgVar: "--cosmic-rain-light-bg",
        darkSvgVar: "--cosmic-rain-dark-bg",
    },
    {
        id: "lava-lamp",
        name: "Lava Lamp",
        lightSvgVar: "--lava-lamp-light-bg",
        darkSvgVar: "--lava-lamp-dark-bg",
    },
    {
        id: "aqua-prisma",
        name: "Aqua Prisma",
        lightSvgVar: "--aqua-prisma-light-bg",
        darkSvgVar: "--aqua-prisma-dark-bg",
    },
    {
        id: "plain_gradient",
        name: "Minimal Gradient",
        lightSvgVar: "--plain-gradient-light-svg-data",
        darkSvgVar: "--plain-gradient-dark-svg-data",
        isDynamic: true,
    },
];

const getBackgroundOptions = (primaryColor: string, isDarkMode: boolean): BackgroundOption[] => {
    return staticBackgroundOptions.map((option) => {
        if (option.id === "plain_gradient") {
            return {
                ...option,
                previewLightSvg: generatePlainGradientSvg(primaryColor, false),
                previewDarkSvg: generatePlainGradientSvg(primaryColor, true),
            };
        }
        return {
            ...option,
            previewLightSvg: `var(${option.lightSvgVar})`, // Use CSS var for preview of static themes
            previewDarkSvg: `var(${option.darkSvgVar})`, // Use CSS var for preview of static themes
        };
    });
};

// 3. Define the context type
interface BackgroundContextType {
    selectedBackgroundId: string;
    setSelectedBackgroundId: (id: string) => void;
    availableBackgrounds: BackgroundOption[];
}

// 4. Create the context
const BackgroundContext = createContext<BackgroundContextType | undefined>(undefined);

// 5. Create the provider component
interface BackgroundProviderProps {
    children: ReactNode;
}

export const BackgroundProvider: React.FC<BackgroundProviderProps> = ({ children }) => {
    const { primaryColor, isDarkMode } = useTheme();
    const [selectedBackgroundId, setSelectedBackgroundIdState] = useState<string>(() => {
        const storedId = localStorage.getItem("selectedBackgroundId");
        return storedId || "plain_gradient";
    });

    const availableBackgrounds = useMemo(
        () => getBackgroundOptions(primaryColor, isDarkMode),
        [primaryColor, isDarkMode],
    );

    // Effect 1: Handles setting the background image (dynamic or static)
    useEffect(() => {
        const currentAvailableBackgrounds = getBackgroundOptions(primaryColor, isDarkMode);
        const selectedOption = currentAvailableBackgrounds.find(
            (opt) => opt.id === selectedBackgroundId,
        );
        if (!selectedOption) return;

        if (selectedOption.isDynamic) {
            // For dynamic backgrounds, the image depends on the color and theme
            const lightSvgData = generatePlainGradientSvg(primaryColor, false);
            const darkSvgData = generatePlainGradientSvg(primaryColor, true);

            // Set the --current-background-image directly
            document.documentElement.style.setProperty(
                "--current-background-image-light",
                lightSvgData,
            );
            document.documentElement.style.setProperty(
                "--current-background-image-dark",
                darkSvgData,
            );
        } else {
            // For static backgrounds, it only depends on the selection
            document.documentElement.style.setProperty(
                "--current-background-image-light",
                `var(${selectedOption.lightSvgVar})`,
            );
            document.documentElement.style.setProperty(
                "--current-background-image-dark",
                `var(${selectedOption.darkSvgVar})`,
            );
        }
    }, [selectedBackgroundId, primaryColor, isDarkMode]);

    // Effect 2: Persists the user's background choice to localStorage
    useEffect(() => {
        localStorage.setItem("selectedBackgroundId", selectedBackgroundId);
    }, [selectedBackgroundId]);

    const setSelectedBackgroundId = (id: string) => {
        setSelectedBackgroundIdState(id);
    };

    return (
        <BackgroundContext.Provider
            value={{
                selectedBackgroundId,
                setSelectedBackgroundId,
                availableBackgrounds,
            }}
        >
            {children}
        </BackgroundContext.Provider>
    );
};

// 6. Create a custom hook for using the context
export const useBackground = (): BackgroundContextType => {
    const context = useContext(BackgroundContext);
    if (!context) {
        throw new Error("useBackground must be used within a BackgroundProvider");
    }
    return context;
};
